/**
 * AnchorMarker Component
 *
 * Displays center point (pin) that shows where the map will focus when task is opened.
 * This is the Single Source of Truth (SSOT) for task positioning.
 *
 * Features:
 * - Always draggable (independent of global drag mode)
 * - Pin icon (📍) without circular background
 * - Orange/red color for visibility
 * - No context menu (cannot be deleted via PPM)
 * - Triggers GPS detection on drag end
 * - Zoom-responsive sizing
 * - Only visible in create/edit mode
 *
 * @module components/map/AnchorMarker
 */

import { memo, useCallback } from 'react';
import { Marker } from '@vis.gl/react-maplibre';
import type { MarkerDragEvent } from '@vis.gl/react-maplibre';
import { IconMapPinFilled } from '@tabler/icons-react';
import type { Coordinates } from '../../../types';
import { ANCHOR_MIN_SIZE_PX, ANCHOR_MAX_SIZE_PX, ANCHOR_ZOOM_MULTIPLIER } from '../../../constants';
import classes from './AnchorMarker.module.css';

/**
 * Props for AnchorMarker component
 */
export interface AnchorMarkerProps {
  /** Anchor point coordinates */
  coordinates: Coordinates;
  /** Current map zoom level for responsive sizing */
  zoom: number;
  /** Callback when anchor is dragged to new position */
  onDragEnd: (coords: Coordinates) => void;
}

/**
 * AnchorMarker Component
 *
 * Renders center point pin that shows where map will focus when task opens.
 * Always draggable and triggers GPS detection.
 */
export const AnchorMarker = memo(function AnchorMarker({
  coordinates,
  zoom,
  onDragEnd,
}: AnchorMarkerProps) {
  const handleDragEnd = useCallback(
    (event: MarkerDragEvent) => {
      onDragEnd({
        lat: event.lngLat.lat,
        lng: event.lngLat.lng,
      });
    },
    [onDragEnd]
  );

  // Zoom-responsive size - bigger than regular markers for visibility
  const size = Math.max(ANCHOR_MIN_SIZE_PX, Math.min(ANCHOR_MAX_SIZE_PX, zoom * ANCHOR_ZOOM_MULTIPLIER));

  return (
    <Marker
      longitude={coordinates.lng}
      latitude={coordinates.lat}
      anchor="bottom" // Pin anchored at bottom (tip of the pin)
      draggable={true} // ALWAYS draggable (not affected by global drag mode)
      onDragEnd={handleDragEnd}
    >
      <div className={classes.pin}>
        <IconMapPinFilled size={size} />
      </div>
    </Marker>
  );
});
